import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { Defer20220824Handler } from '@apollo/client/incremental';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { ApolloProvider } from '@apollo/client/react';
import Bugsnag from '@bugsnag/js';
import { sha256 } from 'crypto-hash';
import { getDefaultStore } from 'jotai';
import { debounce } from 'lodash';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import {
  currentCustomerAtom,
  customerIdMismatchAtom,
  releaseTogglesDisabledAtom,
} from 'utils/account/atoms';
import { AUTH0_SCOPE, useAuthContext } from 'utils/auth';
import { notifyBugsnag } from 'utils/bugsnag';
import { APP_NAME, APP_VERSION, ATLAS_API_HOSTNAME } from 'utils/env';

import ApolloContext from './ApolloContext';
import { ORG_ACCESS_DENIED_MESSAGE, STARGATE_UNAUTHORIZED_MESSAGE } from './isAuthError';

const orgMismatchMsg = debounce(() => {
  console.info(
    "One or more GraphQL queries have been ignored because there's a descrepancy in customer IDs. If you are stuck, close this browser tab and open a new Horizon tab."
  );
}, 500);

// Module-level shared Promise: collapses concurrent forced token refreshes into
// one in-flight request, including across clients rebuilt by useMemo after an
// auth context refresh. The first caller's getAccessTokenSilently reference is
// used for any in-flight promise.
let pendingForcedTokenRefresh = null;

function forcedTokenRefresh(getAccessTokenSilently) {
  if (!pendingForcedTokenRefresh) {
    pendingForcedTokenRefresh = getAccessTokenSilently({
      cacheMode: 'off',
      authorizationParams: { scope: AUTH0_SCOPE },
    }).finally(() => {
      pendingForcedTokenRefresh = null;
    });
  }
  return pendingForcedTokenRefresh;
}

// Module-level debounced reauth: shared across all getClient calls so the
// debounce timer is not reset when useMemo rebuilds clients after an auth
// context refresh. Without this, two near-simultaneous 401s that straddle a
// rebuild boundary could both fire and trigger duplicate redirects.
let debouncedReauth = null;

export function getClient({
  apiHostName,
  cacheOptions = {},
  getAuthHeader,
  getAccessTokenSilently,
  historyReplace,
}) {
  const jotaiStore = getDefaultStore();

  if (!debouncedReauth) {
    debouncedReauth = debounce(() => historyReplace('/auth_redirect'), 500);
  }

  const errorLink = onError(({ error, operation, forward }) => {
    Bugsnag.addMetadata('GraphQL', { operationName: operation.operationName });

    if (CombinedGraphQLErrors.is(error)) {
      const unauthorized = error.errors.some(graphQLError => {
        const message = `[GraphQL error]: ${JSON.stringify(graphQLError)}`;
        return (
          // Stargate returns 401s like this. only treat as unauthorized when the 401
          // does not come from the `GetNotifications` GQL request
          (message.includes(STARGATE_UNAUTHORIZED_MESSAGE) &&
            operation.operationName !== 'GetNotifications') ||
          // Some subgraphs reject a missing org grant with this message instead of a 401
          message.includes(ORG_ACCESS_DENIED_MESSAGE)
        );
      });

      if (unauthorized) {
        // 401s during an org switch are expected — in-flight requests may complete
        // against the wrong org. The app will reinitialize on its own; don't log out.
        const customerIdMismatch = jotaiStore.get(customerIdMismatchAtom);
        if (customerIdMismatch) {
          Bugsnag.clearMetadata('GraphQL');
          return new Observable(observer => observer.complete());
        }

        // Only retry once — if the refreshed token still gets a 401, redirect to re-auth.
        if (operation.getContext().retried) {
          console.warn('Token refresh did not resolve 401, redirecting to re-auth');
          notifyBugsnag(
            `Token refresh did not resolve 401 for operation: ${operation.operationName}`
          );
          debouncedReauth();
          Bugsnag.clearMetadata('GraphQL');
          // Propagate the error so Apollo surfaces it to the component rather
          // than silently completing with no data, which can leave the UI in a
          // permanent loading state during the debounce window before redirect.
          return new Observable(observer => observer.error(error));
        }

        // Attempt a forced token refresh and retry the operation. This handles the
        // common case where the access token expired between the last silent refresh
        // and the request completing (including the refresh token rotation race with
        // multiple open tabs).
        console.warn('User is unauthorized, attempting token refresh before redirecting');
        return new Observable(observer => {
          let cancelled = false;
          let retrySub = null;
          forcedTokenRefresh(getAccessTokenSilently)
            .then(token => {
              if (cancelled) {
                return;
              }
              // authLink sits before errorLink in the chain, so forward(operation)
              // here continues at retryLink → httpLink and does not re-run authLink.
              // We must inject the fresh token manually so the retried request is
              // authenticated.
              const { headers } = operation.getContext();
              operation.setContext({
                headers: { ...headers, Authorization: `Bearer ${token}` },
                retried: true,
              });
              retrySub = forward(operation).subscribe(observer);
            })
            .catch(() => {
              if (cancelled) {
                return;
              }
              // Token refresh failed — redirect to re-auth rather than hard logout.
              notifyBugsnag(
                `getAccessTokenSilently failed for operation: ${operation.operationName}`
              );
              debouncedReauth();
              Bugsnag.clearMetadata('GraphQL');
              observer.complete();
            });
          // Cleanup - cancel on unmount
          return () => {
            cancelled = true;
            retrySub?.unsubscribe();
          };
        });
      }

      error.errors.forEach(graphQLError => {
        const message = `[GraphQL error]: ${JSON.stringify(graphQLError)}`;
        console.error(message);
        notifyBugsnag(message);
      });
      Bugsnag.clearMetadata('GraphQL');
    } else if (navigator.onLine && error) {
      console.error(`[Network error (${operation.operationName})]: ${error}`);
      setTimeout(() => {
        notifyBugsnag(new Error(`[GraphQL Network error (${operation.operationName})]: ${error}`));
        Bugsnag.clearMetadata('GraphQL');
      }, 10000);
    }
  });

  const retryLink = new RetryLink({
    // @see https://www.apollographql.com/docs/react/api/link/apollo-link-retry/
    attempts: (count, operation) => {
      // it is assumed that "retry" attempts are additional request attempts.
      // e.g. if `retryAttempts` is 0, that means, only run the request once. if
      // `retryAttempts` is 1, only run the request up to 2 times (initial
      // request + 1 retry).
      //
      // the default is 3 attempts (aka, 2 retry attempts)
      const contextualRetryAttempts = operation.getContext().retryAttempts;
      const retryAttempts = contextualRetryAttempts ?? 2;

      if (
        operation.query.kind === 'Document' &&
        operation.query.definitions[0].operation === 'mutation' &&
        typeof contextualRetryAttempts === 'undefined'
      ) {
        // it could be problematic if mutations are retried, so don't do it by
        // default
        return false;
      }

      return count < retryAttempts + 1;
    },
  });

  const authLink = new ApolloLink((operation, forward) => {
    return new Observable(observer => {
      getAuthHeader()
        .then(authHeader => {
          const { headers } = operation.getContext();
          operation.setContext({
            headers: {
              ...headers,
              ...authHeader,
            },
          });
          // return the headers to the context so httpLink can read them
          forward(operation).subscribe(observer);
        })
        .catch(err => observer.error(err));
    });
  });

  // this link will determine if there's a mismatch between the currently-viewed
  // organization and the organization in local storage. these may get out of
  // sync if multiple browser tabs are open and the user changes orgs on one of
  // them, causing a discrepancy across tabs. when there's a mismatch, this link
  // will prevent any queries or mutations from being submitted.
  const orgMismatchLink = new ApolloLink((operation, forward) => {
    const customerIdMismatch = jotaiStore.get(customerIdMismatchAtom);

    if (customerIdMismatch) {
      // returning without forward() will prevent the operation from being executed
      // @see https://www.apollographql.com/docs/react/api/link/introduction/#the-forward-function
      orgMismatchMsg();
      return;
    }

    return forward(operation);
  });

  const getHeader = (name, value) => (value ? { [name]: value } : undefined);

  const customerLink = new ApolloLink((operation, forward) => {
    const customerId = jotaiStore.get(currentCustomerAtom);
    if (!customerId) {
      return forward(operation);
    }

    const { headers } = operation.getContext();
    const disabledReleaseToggles = jotaiStore.get(releaseTogglesDisabledAtom);

    operation.setContext({
      headers: {
        ...headers,
        ...getHeader('X-SkySpecs-Customer', customerId),
        ...getHeader('X-SkySpecs-IgnoreToggles', disabledReleaseToggles?.join(',')),
      },
    });

    return forward(operation);
  });

  const httpLink = new HttpLink({
    uri: `${apiHostName}/graphql`,
    preserveHeaderCase: true,
  });

  const cache = new InMemoryCache({
    possibleTypes: {
      // Asset is an interface; cached objects are Site/Turbine/Blade/GenericAsset.
      // Named fragments `on Asset` (e.g. AssetMakeModelFieldValuesFragment) only
      // reattach fieldValues on cache read when these concrete types are listed.
      Asset: ['Site', 'Turbine', 'Blade', 'GenericAsset'],
      Task: [
        'RepairTask',
        'InspectTask',
        'DamageRepairTask',
        'DamageInspectTask',
        'OtherTask',
        'OtherAssetTask',
        'InternalBladeInspectionTask',
        'InspectionTask',
      ],
      DamageSchemaAttributeDefinition: [
        'DamageSchemaAttributeDefinitionString',
        'DamageSchemaAttributeDefinitionNumber',
        'DamageSchemaAttributeDefinitionDate',
      ],
      DamageSchemaConstraintCriteria: [
        'DamageSchemaConstraintCriteriaString',
        'DamageSchemaConstraintCriteriaNumber',
        'DamageSchemaConstraintCriteriaDate',
      ],
      DamageSchemaAttributeHistorical: [
        'DamageSchemaAttributeHistoricalNumber',
        'DamageSchemaAttributeHistoricalString',
        'DamageSchemaAttributeHistoricalDate',
      ],
      AssetFieldValue: [
        'AssetFieldValueText',
        'AssetFieldValueNumber',
        'AssetFieldValueSelect',
        'AssetFieldValueMultiSelect',
        'AssetFieldValueDate',
        'AssetFieldValueFloat',
        'AssetFieldValueBool',
      ],
    },
    typePolicies: {
      Query: {
        fields: {
          alerts: {
            merge: false,
          },
          getHorizonDamages: {
            // Must merge, not replace. `deferCount` requests this field twice with identical
            // arguments (`@include` sits on the `items`/`totalCount` subfields), so both responses
            // write to the same cache entry — replacing made each erase the other's half, leaving
            // both reads incomplete and both queries refetching forever.
            merge: (existing, incoming) => ({ ...existing, ...incoming }),
          },
          unplannedWorkOrders: {
            merge: (_existing, incoming) => incoming,
          },
        },
      },
      Image: {
        keyFields: false,
      },
      CD_Image: {
        keyFields: false,
      },
      GlobalWorkContainerField: {
        // disables normalization, embeds GlobalWorkContainerField within parent object in cache.
        keyFields: _field => false,
      },
      Picture: {
        fields: {
          damages: {
            merge: false,
          },
          image: {
            merge: true,
          },
          source: {
            merge: true,
          },
          target: {
            merge: true,
          },
        },
      },
      AnnotationObservation: {
        fields: {
          damages: {
            merge: false,
          },
        },
      },
      AnnotationGroup: {
        merge: false,
      },
      CD_Location: {
        fields: {
          turbines: {
            merge: false,
          },
        },
      },
      // prevent apollo client from caching cost resource field definitions
      CostResourceFieldDefinition: {
        keyFields: false,
      },
      // prevent apollo client from caching role objects by id since id's aren't unique yet
      Role: {
        keyFields: _field => false,
      },
      RecordPermission: {
        keyFields: ['orgId', 'userId', 'recordType'],
      },
      CD_Role: {
        keyFields: _field => false,
      },
      AssetFieldDefinition: {
        keyFields: ['id'],
        merge: false,
      },
      AssetFieldValue: {
        keyFields: ['id', 'fieldDefinition', ['id']],
      },
      NotificationTypeMatchCriteria: {
        keyFields: ['criteriaName'],
      },
      NotificationSubscriptionMatchCriteria: {
        keyFields: ['notificationTypeMatchCriteria', ['criteriaName']],
        merge: true,
      },
      // prevent apollo client from caching damage schema attribute historical objects since might not be unique
      // same attribute might appear in different schemas with same id but different data (options/ranges)
      DamageSchemaAttributeHistoricalString: { keyFields: false },
      DamageSchemaAttributeHistoricalNumber: { keyFields: false },
      DamageSchemaAttributeHistoricalDate: { keyFields: false },
    },
    ...cacheOptions,
  });

  const defaultOptions = {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: false,
  };

  const links = [
    authLink,
    orgMismatchLink,
    customerLink,
    errorLink,
    retryLink,
    createPersistedQueryLink({ sha256 }),
    httpLink,
  ];

  const client = new ApolloClient({
    link: from(links),
    cache,
    incrementalHandler: new Defer20220824Handler(),
    clientAwareness: {
      name: APP_NAME, // Lets us know what's calling stuff from inside apollo studio
      version: APP_VERSION,
    },
    // connectToDevTools: apiHostName === DEBUG_HOSTNAME, // Uncomment if you want to inspect a specific client's cache using in-browser devTools.
  });

  client.defaultOptions = {
    query: defaultOptions,
    watchQuery: defaultOptions,
  };

  return client;
}

export const ApolloClientProvider = props => {
  const { getAuthHeader, getAccessTokenSilently } = useAuthContext();
  const { replace } = useHistory();

  const atlasClient = useMemo(
    () =>
      getClient({
        apiHostName: ATLAS_API_HOSTNAME,
        getAuthHeader,
        getAccessTokenSilently,
        historyReplace: replace,
      }),
    [getAuthHeader, getAccessTokenSilently, replace]
  );

  const resetCache = useMemo(() => {
    return async () => {
      // clearStore will remove all the data from the store, and will not refetch
      // any active queries.
      // @see https://www.apollographql.com/docs/react/api/core/ApolloClient/#ApolloClient.clearStore
      await atlasClient.clearStore();
    };
  }, [atlasClient]);

  const resetStore = useMemo(() => {
    return async () => {
      // resetStore will remove all the data from the store and then refetch any
      // active queries.
      // @see https://www.apollographql.com/docs/react/api/core/ApolloClient/#ApolloClient.resetStore
      await atlasClient.resetStore();
    };
  }, [atlasClient]);

  return (
    <ApolloContext.Provider value={{ atlasClient, resetCache, resetStore }}>
      <ApolloProvider {...props} client={atlasClient} />
    </ApolloContext.Provider>
  );
};

// Export all methods from a single module
export * from '@apollo/client';
export * from '@apollo/client/react';
